import { useState, useEffect } from "react";
import arrayUnion from "array-union";
import { auth, db, functions, storage } from "./Firebase";
import { AppContext } from "./AppContext";
import { toast } from "react-toastify";

// ANCHOR Update User
const updateUser = (uid, companyId, companyName, firstName, lastName, role) =>
  new Promise((resolve, reject) => {
    const userRef = db.collection("users").doc(uid);
    userRef
      .set(
        {
          company: db.doc(`companies/${companyId}`),
          companyName: companyName,
          name: {
            firstName,
            lastName
          },
          role
        },
        { merge: true }
      )
      .then(user => {
        toast("User Updated", { type: toast.TYPE.INFO });
        resolve(user);
      })
      .catch(error => {
        // Failed to create user record in Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

// ANCHOR Update User Fleets
const updateUserFleets = (uid, fleet, fleetName) =>
  new Promise((resolve, reject) => {
    const userRef = db.collection("users").doc(uid);

    userRef
      .set(
        {
          fleet: fleet,
          fleetName: fleetName
        },
        { merge: true }
      )
      .then(user => resolve(user))
      .catch(error => {
        // Failed to create user record in Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });
// });

// ANCHOR Create User
const createUser = functions.httpsCallable("createUser");

// ANCHOR Get User
const getUser = uid =>
  new Promise((resolve, reject) => {
    const userRef = db.collection("users").doc(uid);

    userRef.onSnapshot(userDoc => {
      const userObj = userDoc.data();
      userObj.fullName = `${userObj.name.firstName} ${userObj.name.lastName}`;
      resolve(userObj);
    });

    // userRef
    //   .get()
    //   .then(userDoc => resolve(userDoc.data()))
    //   .catch(error => {
    //     // Failed to fetch user from Firebase.
    //     const errorCode = error.code;
    //     const errorMessage = error.message;
    //     reject(new Error(`${errorMessage} (${errorCode})`));
    //   });
  });

/**
 * ANCHOR Get Multiple Users
 * Uses role of current user to determine what users will be returned.
 * Get all users within a fleet by passing a fleetId.
 */
const getAllUsers = (company, fleet, role, limit) =>
  new Promise((resolve, reject) => {
    let usersRef = db.collection("users");

    if (company && !fleet) {
      usersRef = usersRef.where("company", "==", db.doc(`companies/${company}`));
    }

    if (role && role !== "" && role !== null) {
      usersRef = usersRef.where("role", "==", role);
    }

    if (limit) {
      usersRef = usersRef.limit(limit);
    }

    usersRef
      .get()
      .then(users => {
        const allUsers = [];

        // eslint-disable-next-line consistent-return, array-callback-return
        users.docs.map((user, index) => {
          const userObj = user.data();
          userObj.fullName = `${userObj.name.firstName} ${userObj.name.lastName}`;
          allUsers.push(userObj);

          if (users.docs.length === index + 1) {
            return resolve(allUsers);
          }
        });
      })
      .catch(error => {
        // Failed to get users in company.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

// ANCHOR Delete User
const deleteUser = functions.httpsCallable("deleteUser");

// ANCHOR Attemp to login user with credentials
const loginUser = (email, password) =>
  new Promise(resolve => {
    auth
      .signInWithEmailAndPassword(email, password)
      // eslint-disable-next-line no-undef
      .then(userAuth => {
        getUser(userAuth.user.uid).then(user => {
          if (user.role === "driver") {
            return resolve("auth/user-not-found");
          } else {
            window.location.reload();
          }
        });
      })
      .catch(error => {
        // Failed to fetch user from Firebase.
        const errorCode = error.code;
        return resolve(errorCode);
      });
  });

// ANCHOR Logout the current user.
const logoutUser = () =>
  new Promise((resolve, reject) => {
    auth
      .signOut()
      // eslint-disable-next-line no-undef
      .then(() => window.location.reload())
      .catch(error => {
        // Failed to logout user.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const useAuth = authentication => {
  const [authState, setAuthState] = useState({
    loading: true,
    user: null
  });

  useEffect(() => {
    authentication.onAuthStateChanged(userAuth => {
      setAuthState({ loading: false, user: userAuth });
    });
  }, [authentication]);

  return authState;
};

const useGetUser = uid => {
  const [user, setUser] = useState({
    loading: true,
    user: null
  });

  useEffect(() => {
    if (uid) {
      const userRef = db.collection("users").doc(uid);

      userRef.onSnapshot(async userDoc => {
        const userObj = userDoc.data();
        userObj.fullName = `${userObj.name.firstName} ${userObj.name.lastName}`;
        if (userObj.company)
          await userObj.company.get().then(company => {
            userObj.company = company.data();
            userObj.companyId = company.id;
          });
        if (userObj.fleet && userObj.role === "driver")
          await userObj.fleet.get().then(fleet => (userObj.fleet = fleet.data()));
        setUser({
          loading: false,
          user: userObj
        });
      });
    } else {
      setUser({
        loading: false,
        user: null
      });
    }
  }, []);

  return user;
};

/**
 * Get Multiple User Documents
 * & Listen for Changes
 */
const useGetAllUsers = (company, fleet, role, limitToFleet, limit) => {
  const [allUsers, setAllUsers] = useState({
    loading: true,
    users: []
  });

  let usersRef = db.collection("users");

  if (company && !fleet) {
    usersRef = usersRef.where("company", "==", db.doc(`companies/${company}`));
  } else if (company && fleet && role && role === "driver") {
    usersRef = usersRef.where("fleet", "==", db.doc(`companies/${company}/fleets/${fleet}`));
  } else if (company && fleet && role && role === "fleet-manager") {
    usersRef = usersRef.where("managingFleets", "array-contains", db.doc(`companies/${company}/fleets/${fleet}`));
  }

  if (role && role !== "" && role !== null && role !== "managers") {
    usersRef = usersRef.where("role", "==", role);
  }

  if (limit) {
    usersRef = usersRef.limit(limit);
  }

  var moment = require("moment");

  useEffect(() => {
    usersRef.onSnapshot(async users => {
      const allUserDocs = [];
      await Promise.all(
        users.docs.map((user, index) => {
          const userObj = user.data();
          userObj.fullName = userObj.name ? `${userObj.name.firstName} ${userObj.name.lastName}` : "";
          if (userObj.role === "driver") {
            var energyLevel = userObj.stats.energyLevel;
            var energyUpdated = moment(userObj.stats.energyUpdated);
            if (energyUpdated.isAfter(moment().subtract(30, "minutes"))) {
              userObj.energyLevel = energyLevel;
            } else {
              userObj.energyLevel = "N/A";
            }
          } else {
            userObj.energyLevel = "N/A";
          }
          allUserDocs.push(userObj);
        })
      );

      return setAllUsers({
        loading: false,
        users: allUserDocs
      });
    });
  }, []);

  return allUsers;
};

const getNotificationsForUser = userId => {
  const [notifications, setNotifications] = useState({ notifications: [] });

  const userRef = db.doc(`users/${userId}`);

  useEffect(() => {
    db.collection("notifications")
      .where("user", "==", userRef)
      .onSnapshot(querySnapshot => setNotifications({ notifications: querySnapshot }));
  }, []);

  return notifications;
};

const triggerPasswordReset = email =>
  new Promise((resolve, reject) => {
    auth
      .sendPasswordResetEmail(email)
      .then(() => resolve("Sent password reset email"))
      .catch(error => reject(error.code));
  });

const getUserPhoto = uid =>
  new Promise((resolve, reject) => {
    const userPhotoRef = storage.ref(uid);
    userPhotoRef
      .getDownloadURL()
      .then(url => resolve(url))
      .catch(error => console.log(error));
  });

export {
  createUser,
  updateUser,
  updateUserFleets,
  getNotificationsForUser,
  getUser,
  getAllUsers,
  deleteUser,
  loginUser,
  logoutUser,
  triggerPasswordReset,
  getUserPhoto,
  // hooks
  useAuth,
  useGetAllUsers,
  useGetUser
};
